import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';

const ZAPIER_ELEMENTS_SCRIPT_ID = 'zapier-elements-script';
const ZAPIER_ELEMENTS_CSS_ID = 'zapier-elements-css';

const Zapier = () => {
    const { accountId } = useCurrentAccount();

    useEffect(() => {
        if (!document.getElementById(ZAPIER_ELEMENTS_SCRIPT_ID)) {
            const script = document.createElement('script');
            script.type = 'module';
            script.id = ZAPIER_ELEMENTS_SCRIPT_ID;
            script.src =
                'https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.esm.js';
            document.head.appendChild(script);
        }

        if (!document.getElementById(ZAPIER_ELEMENTS_CSS_ID)) {
            const stylesheet = document.createElement('link');
            stylesheet.rel = 'stylesheet';
            stylesheet.id = ZAPIER_ELEMENTS_CSS_ID;
            stylesheet.href =
                'https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.css';
            document.head.appendChild(stylesheet);
        }
    }, []);

    return (
        <Box className="integrations flex flex-col gap-6 p-8">
            <div className="flex flex-col items-start gap-4">
                <div className="flex items-center gap-3">
                    <img alt="Zapier" className="max-w-[120px]" src={`/images/logos/zapier.svg`} />

                    <Tooltip.Root>
                        <Tooltip.Trigger>
                            <FontAwesomeIcon className="text-gray-600" icon={faQuestionCircle} />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content inverse>
                                <p>
                                    Givebutter integrates with Zapier for free. Zapier also has a
                                    free plan, however there are usage limits. See{' '}
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href="https://zapier.com/pricing"
                                        className="text-white underline hover:text-white"
                                    >
                                        Zapier's pricing
                                    </a>{' '}
                                    for details.
                                </p>
                                <Tooltip.Arrow inverse />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
                <div>
                    <p className="text-base">
                        Connect to thousands of apps using Zapier’s advanced integration platform.
                    </p>
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://help.givebutter.com/en/articles/3813405-zapier"
                    >
                        Read the integration guide
                    </a>
                </div>

                <div className="flex gap-4">
                    <Link to={`/accounts/${accountId}/settings/integrations/api`}>
                        <Button variant="outline" color="neutral">
                            API key settings
                        </Button>
                    </Link>
                    <a href="https://zapier.com/sign-up" target="_blank" rel="noreferrer">
                        <Button variant="solid">Go to Zapier</Button>
                    </a>
                </div>
            </div>

            <Separator />

            <zapier-full-experience
                client-id={import.meta.env.VITE_DASHBOARD_ZAPIER_EMBED_CLIENT_ID}
                theme="light"
                intro-copy-display="hide"
                app-search-bar-display="show"
                zap-limit={5}
                zap-style="card"
                zap-call-to-action-display="show"
                zap-create-from-scratch-display="show"
            />
        </Box>
    );
};

export { Zapier };
